/* global FB */
import API from '@/services/api'
import fbIcon from '@/assets/images/facebook_icon.png'
import PriceTableModal from '@/components/PriceTableModal.vue'
import AppVersion from '@/components/AppVersion.vue'
import { decodeCredential } from 'vue3-google-login'
import { sessionRecorder, UserEvent } from '@/services/sessionRecorder'
import { useRoute, useRouter } from 'vue-router'
import { captureException } from '@/errors'
import getPlans from '@/views/home/service/getPlans'
import getCustomerPlans from '@/views/home/service/getCustomerPlans'
import getPointByToken from '@/services/getPointName'
import getPointIdFromStorage from '@/services/getPointId'
import { formatPrice } from '@/utils/formatPrice'
import { getPlanSeconds } from '@/utils/planTime'

const GTAG_SOCIAL_LOGIN = 'login_social'
const GTAG_PRICE_TABLE = 'open_price_table'

export default {
  name: 'login-social',
  components: {
    PriceTableModal,
    AppVersion
  },
  props: [],
  data() {
    return {
      fb_icon: fbIcon,
      locale: '',
      showPriceTableModal: false,
      showPrice: false,
      pointName: '',
      plans: null,
      planHourly: null,
      planDaily: null,
      campaign: null
    }
  },
  computed: {
    rentInfoText() {
      const pointName = this.pointName ? ' no ' + this.pointName : ''
      if (!this.plans || (!this.planHourly && !this.planDaily)) {
        return this.$t('login.rent_info', { pointName })
      }

      const campaignFreeTime = this.campaign?.free_time || 0
      const hasFreeTime = campaignFreeTime >= 300
      const freeTimeText = hasFreeTime ? this.formatDurationFromSeconds(campaignFreeTime) : ''

      const hasHourly = !!this.planHourly
      let hourlyTime = ''
      let hourlyPrice = ''
      if (hasHourly) {
        const hourlySeconds = this.getPlanSeconds(this.planHourly)
        hourlyTime = this.formatDurationFromSeconds(hourlySeconds)
        hourlyPrice = this.formatPrice(this.planHourly.amount)
      }

      const hasDaily = !!this.planDaily
      let dailyTime = ''
      let dailyPrice = ''
      if (hasDaily) {
        const dailySeconds = this.getPlanSeconds(this.planDaily)
        dailyTime = this.formatDurationFromSeconds(dailySeconds)
        dailyPrice = this.formatPrice(this.planDaily.amount)
      }

      let key = 'rent_info'
      if (hasHourly && hasDaily) {
        key = hasFreeTime ? 'rent_info_free_hourly_daily' : 'rent_info_hourly_daily'
      } else if (hasDaily) {
        key = hasFreeTime ? 'rent_info_free_daily' : 'rent_info_daily'
      } else if (hasHourly) {
        key = hasFreeTime ? 'rent_info_free_hourly' : 'rent_info_hourly'
      }

      return this.$t(`login.${key}`, {
        pointName,
        freeTime: freeTimeText,
        hourlyTime,
        hourlyPrice,
        dailyTime,
        dailyPrice
      })
    }
  },
  mounted: function () {
    const route = useRoute()
    const router = useRouter()

    const token = route.query.token
    if (token && token !== 'undefined' && token.length > 0) {
      localStorage.setItem('token', token)
      router.push({ path: '/home' })
    }

    this.showPrice = localStorage.getItem('show_payment_info') || false
    this.pointName = localStorage.getItem('point_name') || ''

    this.loadPointPlans()

    window.fbAsyncInit = function () {
      FB.init({
        appId: import.meta.env.VITE_FACEBOOK_ID,
        cookie: true,
        xfbml: true,
        status: true,
        version: import.meta.env.VITE_FACEBOOK_VERSION
      })
    }
    ;(function (d, s, id) {
      let js
      let fjs = d.getElementsByTagName(s)[0]
      if (d.getElementById(id)) return
      js = d.createElement(s)
      js.id = id
      js.src = '//connect.facebook.net/en_US/sdk.js'
      fjs.parentNode.insertBefore(js, fjs)
    })(document, 'script', 'facebook-jssdk')
  },
  methods: {
    async loadPointPlans() {
      try {
        let pointId = getPointIdFromStorage().id
        if (!pointId && localStorage.getItem('point_id')) {
          pointId = parseInt(localStorage.getItem('point_id'))
        }
        if (!pointId) {
          const terminalToken = localStorage.getItem('terminal_token')
          if (terminalToken) {
            const pointInfo = await getPointByToken(terminalToken)
            if (pointInfo?.success && pointInfo?.id) {
              pointId = pointInfo.id
            }
          }
        }
        if (!pointId) return

        const userToken = localStorage.getItem('token') || null
        if (userToken) {
          this.plans = await getCustomerPlans(pointId)
        } else {
          this.plans = await getPlans(pointId, false)
        }

        if (this.plans) {
          this.parsePlansData()
        }
      } catch (error) {
        captureException(error)
      }
    },
    parsePlansData() {
      if (this.plans?.price) {
        this.planHourly = this.plans.price.find(p => p.internal_ref === 'four_hours') || null
        if (this.planHourly && this.planHourly.price_variation) {
          this.planHourly = this.applyPriceVariation(this.planHourly)
        }

        this.planDaily = this.plans.price.find(p => p.internal_ref === 'daily') || null
        if (this.planDaily && this.planDaily.price_variation) {
          this.planDaily = this.applyPriceVariation(this.planDaily)
        }

        if (this.plans.campaign && this.plans.campaign.free_time >= 300) {
          this.campaign = this.plans.campaign
        }
      } else if (this.plans?.on_demand?.prices) {
        this.planHourly = this.plans.on_demand.prices.find(p => p.internal_ref === 'four_hours') || null
        this.planDaily = this.plans.on_demand.prices.find(p => p.internal_ref === 'daily') || null
      }
    },
    applyPriceVariation(price) {
      const variationFactor = 1 + price.price_variation / 100
      const totalCentavos = Math.round(
        (parseInt(price.amount.reais) * 100 + parseInt(price.amount.centavos)) * variationFactor
      )
      price.amount.reais = Math.floor(totalCentavos / 100)
      price.amount.centavos = totalCentavos % 100
      return price
    },
    formatPrice,
    getPlanSeconds,
    formatDurationFromSeconds(totalSeconds) {
      const hours = Math.floor(totalSeconds / 3600)
      const minutes = Math.floor((totalSeconds % 3600) / 60)
      const seconds = totalSeconds % 60

      const parts = []
      if (hours > 0) parts.push(`${hours}${this.$t('price_table.hours')}`)
      if (minutes > 0) parts.push(`${minutes} ${minutes === 1 ? 'minuto' : 'minutos'}`)
      if (seconds > 0) parts.push(`${seconds} ${seconds === 1 ? 'segundo' : 'segundos'}`)

      if (parts.length === 0) {
        return '0 minutos'
      } else if (parts.length === 1) {
        return parts[0]
      } else {
        const lastPart = parts.pop()
        return parts.join(', ') + ' e ' + lastPart
      }
    },
    callback(response) {
      this.googleLoginResponse(response)
    },
    async googleLoginResponse(response) {
      const responsePayload = decodeCredential(response.credential)
      try {
        const responseLogin = await API.post('social/login', {
          email: responsePayload.email,
          name: responsePayload.name,
          social_id: responsePayload.sub,
          from_social: 'google',
          auto_confirm: true
        })
        if (responseLogin.data.success) {
          localStorage.setItem('token', responseLogin.data.token)
          const params = {
            access_type: 'google'
          }
          sessionRecorder.addUserEvent(new UserEvent(GTAG_SOCIAL_LOGIN, null, params))
          this.$router.push({
            name: 'home_page',
            params: {}
          })
        } else {
          sessionRecorder.addUserEvent(
            new UserEvent(GTAG_SOCIAL_LOGIN, null, { access_type: 'google', error: JSON.stringify(responseLogin) })
          )
        }
      } catch (error) {
        captureException(error)
      }
    },
    handleShowPriceTableModal(data) {
      if (data) {
        const token = localStorage.getItem('token')
        const isLoggedIn = !!token && token !== 'undefined' && token !== 'null'
        const params = {
          link_source: isLoggedIn ? 'logged' : 'not_logged'
        }
        sessionRecorder.addUserEvent(new UserEvent(GTAG_PRICE_TABLE, null, params))
      }
      this.showPriceTableModal = data
    },

    handleCampaign: function () {
      const campaignUrl = sessionStorage.getItem('campaignUrl')
      if (campaignUrl) {
        this.$router.push(campaignUrl)
      }
    },
    onFacebookSignInSuccess(response) {
      if (response && response.authResponse && response.authResponse.userID) {
        if (response.authInfo && response.authInfo.email && response.authInfo.email.length > 0) {
          this.handleFacebookUserData(response.authResponse.userID)
        } else {
          this.reRequestEmailPermission()
        }
      }
    },

    handleFacebookUserData(userID) {
      FB.api('me?fields=name,email', user => {
        if (user && user.error) {
          sessionRecorder.addUserEvent(
            new UserEvent(GTAG_SOCIAL_LOGIN, null, { access_type: 'facebook', error: user.error })
          )
          return
        }
        API.post('social/login', {
          name: user.name,
          email: user.email,
          social_id: userID,
          from_social: 'facebook',
          auto_confirm: true
        })
          .then(res => {
            if (res.data.success) {
              localStorage.setItem('token', res.data.token)
              const params = {
                access_type: 'facebook'
              }
              sessionRecorder.addUserEvent(new UserEvent(GTAG_SOCIAL_LOGIN, null, params))
              this.$router.push({ name: 'home_page', params: {} })
            } else {
              sessionRecorder.addError(
                `Failed to login facebook ${JSON.stringify(res.data)}, calling #handleFacebookUserData()`
              )
            }
          })
          .catch(error => {
            captureException(error)
          })
      })
    },

    reRequestEmailPermission() {
      FB.login(
        response => {
          if (response && response.authResponse && response.authResponse.userID) {
            this.handleFacebookUserData(response.authResponse.userID)
          } else {
            sessionRecorder.addUserEvent(
              new UserEvent(GTAG_SOCIAL_LOGIN, null, {
                access_type: 'facebook',
                error: 'User declined email permission'
              })
            )
            console.error('User declined email permission again.')
          }
        },
        { scope: 'email', auth_type: 'rerequest' }
      )
    },

    onFacebookError: function (response) {
      sessionRecorder.addUserEvent(
        new UserEvent(GTAG_SOCIAL_LOGIN, null, { access_type: 'facebook', error: JSON.stringify(response) })
      )
    }
  }
}
